import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { cloneDeep } from 'lodash-es'
import { computed, ref } from 'vue'
import useHelpdeskCustomFields from '@/modules/shared/filters/useHelpdeskCustomFields'
import { session } from '@/composables/useSession'

import i18n from '@/i18n'
import { translationExists } from '@/i18n/utils'
import FilterGroup from '@/components/filters/FilterGroup.vue'
import {
  type ValueComponentProps,
  FilterValueType,
  type FilterOption,
  type FilterItemEmit,
  type AddedFilter,
} from '@/components/filters/types'

import type { DropdownOption, DeconstructedOption } from '../../utils/filters'
import { type DefaultFilterOption } from '../../api/filters'
import {
  fieldsMapToFieldItems,
  getDescriptionKey,
  getDescriptionOptions,
  getEmptyFilterOption,
  getFilterConditionsByOptionName,
} from './utils'

const HELPDESK_CUSTOM_FIELD = 'TICKET_FIELD'


export default /*@__PURE__*/_defineComponent({
  __name: 'HelpdeskFilterGroup',
  props: {
    addedFilters: {},
    options: {},
    selectedOptionNames: {}
  },
  emits: ["filter-adding-started", "filter-applied", "add-similar-filter", "remove"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const filterBeingAdded = ref<DeconstructedOption | null>(null)
const translatedFilterBeingAdded = ref<AddedFilter | null>(null)

const workspaceIds = computed(() => [session.workspace.id])
const helpdeskCustomFieldsOptions = computed(() => ({
  ids: props.addedFilters.map((f) => f.option.name),
}))
const { fieldsMap, search, total, fetchNextPage, selectedFieldsFetched } = useHelpdeskCustomFields(
  workspaceIds,
  helpdeskCustomFieldsOptions,
)

const translateLabel = (opt: DeconstructedOption['option']) => {
  let label = ''
  // this fn is also used for translating conditions, so we need to check if it's a custom field
  if (opt?.isCustom && opt?.name) label = fieldsMap.value?.[opt.name]?.name || ''
  else if (opt?.label) label = translationExists(opt.label) ? i18n.t(opt.label) : opt.label
  return label
}

const addLabel = (opt: DeconstructedOption['option']): FilterOption => {
  // @ts-expect-error not sure if opt can be undefined
  if (!opt || !Object.keys(opt).length) return opt

  const descriptionKey = getDescriptionKey(opt)

  return {
    ...opt,
    name: opt.name || '',
    label: translateLabel(opt),
    description: translationExists(descriptionKey) ? i18n.t(descriptionKey, getDescriptionOptions(opt)) : undefined,
  }
}

const filterOptions = computed(() => {
  const items = fieldsMapToFieldItems(fieldsMap.value)
  const fieldItems = items.sort((a, b) => a.label.localeCompare(b.label))

  return fieldItems.map((o: DropdownOption) => addLabel(o))
})

const toFilterGroupItem = (d: DeconstructedOption) => {
  const descriptionKey = getDescriptionKey(d.option)

  const translatedOption = addLabel(d.option)
  return {
    id: d.id,
    label: translatedOption.label,
    name: translatedOption.name,
    showAddSimilarButton: !!filterOptions.value.find((o) => o?.name === translatedOption.name),
    description: translationExists(descriptionKey)
      ? i18n.t(descriptionKey, getDescriptionOptions(translatedOption))
      : undefined,
    selectedCondition: addLabel(d.condition),
    conditions: getFilterConditions(),
    value: d.values as any,
    valueType: FilterValueType.CustomField,
    valueComponentProps: undefined as ValueComponentProps,
    intelligenceIcon: false,
    botIcon: false,
    disabled: false,
  }
}

const getFilterConditions = () => {
  const filterConditions = getFilterConditionsByOptionName(HELPDESK_CUSTOM_FIELD, props.options)
  return filterConditions.map((c) => addLabel(c))
}

const selectedFilters = computed(() => {
  // wait for selected fields to be fetched before rendering, otherwise label will end up as empty string and we'll have duplicate fields
  return selectedFieldsFetched.value ? props.addedFilters : []
})

const allAddedFilters = computed(() => {
  return [...selectedFilters.value, filterBeingAdded.value].filter(Boolean) as DeconstructedOption[]
})

const filterGroupItems = computed(() => {
  const selectedFilterGroupItems = selectedFilters.value.map(toFilterGroupItem)
  return [...selectedFilterGroupItems, translatedFilterBeingAdded.value].filter(Boolean) as AddedFilter[]
})

const clearFilterBeingAdded = () => {
  filterBeingAdded.value = null
  translatedFilterBeingAdded.value = null
}

const filterAddingStarted = (obj: any) => {
  filterBeingAdded.value = getEmptyFilterOption()

  filterBeingAdded.value.option = obj
  translatedFilterBeingAdded.value = toFilterGroupItem(filterBeingAdded.value)
  emit('filter-adding-started', obj)
}

const handleRemove = (id: string) => {
  if (filterBeingAdded.value?.id === id) {
    clearFilterBeingAdded()
  } else {
    emit('remove', id)
  }
}

const handleFilterApplied = (id: string, f: FilterItemEmit) => {
  const filter = cloneDeep(allAddedFilters.value.find((f) => f.id === id))
  filter.condition = f.selectedCondition
  filter.values = f.value

  if (filterBeingAdded.value?.id === id) {
    clearFilterBeingAdded()
  }

  emit('filter-applied', filter)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(FilterGroup, {
    "has-pagination": "",
    "added-filters": filterGroupItems.value,
    "filter-options": filterOptions.value,
    total: _unref(total),
    onFilterAddingStarted: filterAddingStarted,
    onFilterApplied: handleFilterApplied,
    onAddSimilarFilter: _cache[0] || (_cache[0] = (id, f) => _ctx.$emit('add-similar-filter', id, f)),
    onRemove: handleRemove,
    onSearchChange: _unref(search),
    onLoadMore: _unref(fetchNextPage)
  }, null, 8 /* PROPS */, ["added-filters", "filter-options", "total", "onSearchChange", "onLoadMore"]))
}
}

})